import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { settingsApi } from '../../api/settings_api';
import type { BillingInfo } from '../../types';

export const SettingsBilling = observer(() => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);

  useEffect(() => {
    const fetchBillingInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        const info = await settingsApi.getBillingInfo();
        setBillingInfo(info);
      } catch (err) {
        setError('Failed to load billing information');
        console.error('Error fetching billing info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBillingInfo();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Billing
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage your subscription, payment methods, and billing history.
        </Typography>

        {billingInfo?.has_billing && billingInfo.billing_portal_url ? (
          <Box>
            <Button
              variant="contained"
              color="primary"
              href={billingInfo.billing_portal_url}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon />}
            >
              Manage Billing
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              You will be redirected to {billingInfo.subscription_service === 'stripe' ? 'Stripe' : 'Recurly'} to manage your billing.
            </Typography>
          </Box>
        ) : (
          <Alert severity="info">
            No billing information on file. If you believe this is an error, please contact support.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
});
